// Hooks against the commodities / commodity_varieties / commodity_sizes
// tables (migration 112) plus the commodity_grades / commodity_pack_formats
// registries (migration 832). Replaces the hard-coded COMMODITIES list in
// src/lib/commodities.ts and the LEMON_SIZES / APPLE_SIZES / GRADES /
// *_PACK_TYPES constants in Pallets / Rebin / QC.

import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { countOrThrow } from '@/lib/readState'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { drenchForLine } from '@/lib/intakePath'
import { useBlocks, type Block } from '@/hooks/useGrowerData'

export interface Commodity {
  commodity_id: string
  name:         string
  code:         string | null
  sort_order:   number
  is_active:    boolean
  // The production flow this commodity runs (mig 20260715170000, FCX-26 M1).
  // null = unclaimed → flow resolvers fall back to the tenant's default, i.e.
  // pre-M1 behaviour. This is what lets one packhouse run several flows.
  flow_key:     string | null
  // Deliveries of this fruit go through the packhouse drench (mig
  // 20261025191001). false = the intake form doesn't ask and the load goes
  // straight to the cold room. Absent before that migration is applied, so
  // read it through commodityUsesDrench(), which treats absent as true.
  uses_drench?: boolean
}

export interface CommodityVariety {
  variety_id:        string
  commodity_id:      string
  name:              string
  code:              string | null
  sort_order:        number
  is_active:         boolean
  // Where pallets of this variety go after build-up (mig 205). NULL =
  // operator chooses; 'COLDROOM' = coldroom default + skip override;
  // 'LOADING_BAY' = straight to loading bay.
  default_placement: 'COLDROOM' | 'LOADING_BAY' | null
}

export interface CommoditySize {
  size_id:          string
  commodity_id:     string
  size_code:        string
  diameter_min_mm:  number | null
  diameter_max_mm:  number | null
  reference_count:  number | null
  sort_order:       number
  is_active:        boolean
}

// An intake grading class (mig 20260813100000) — how a load is split by size
// when it is RECEIVED, as opposed to CommoditySize which for berries is the
// punnet/pack format the fruit is later packed into.
interface CommodityIntakeSize {
  intake_size_id: string
  commodity_id:   string
  code:           string
  sort_order:     number
  is_active:      boolean
}

export interface CommodityGrade {
  grade_id:     string
  commodity_id: string
  code:         string
  sort_order:   number
  is_active:    boolean
}

export interface CommodityPackFormat {
  format_id:    string
  commodity_id: string
  code:         string
  label:        string | null
  // Which pallet Category shows this format: EXPORT / LOCAL pallets only,
  // or ANY for both (blueberry punnets don't split by channel).
  channel:      'EXPORT' | 'LOCAL' | 'ANY'
  sort_order:   number
  is_active:    boolean
}

const STALE = 5 * 60_000

// All active commodities, ordered.
export function useCommodities() {
  return useQuery({
    queryKey: ['commodities'],
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodities')
        .select('*')
        .eq('is_active', true)
        .order('sort_order')
      if (error) throw error
      return data as Commodity[]
    },
  })
}

/** The ticked rows of tenant_commodities for one business — ONE read, shared by every screen that asks (same key as the offline prewarm 'tenant-commodities'). */
export function useTenantCommodityRows(tenantId: string | null) {
  return useQuery({
    queryKey: ['tenant-commodities', tenantId],
    enabled: !!tenantId,
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_commodities')
        .select('commodity_id')
        .eq('tenant_id', tenantId!)
      if (error) throw error
      return (data ?? []) as { commodity_id: string }[]
    },
  })
}

// The active tenant's ENABLED commodities (mig 1041 — audit #13). Returns the
// same shape as useCommodities() but filtered to tenant_commodities. An EMPTY
// enabled set (un-configured tenant, or cold offline cache) returns the full
// list, so pickers never go blank and un-configured tenants are unaffected.
// Use this for commodity *pick-lists*; keep useCommodities() for the registry
// editors (CommoditiesAdmin / platform CommodityFlows).
export function useEnabledCommodities() {
  const commoditiesQ = useCommodities()
  const tenantId = useActiveTenantId()
  const enabledQ = useTenantCommodityRows(tenantId)
  const data = useMemo(() => {
    const all = commoditiesQ.data ?? []
    const enabled = enabledQ.data ?? []
    if (enabled.length === 0) return all   // empty / offline-cold → show all
    const ids = new Set(enabled.map(e => e.commodity_id))
    const filtered = all.filter(c => ids.has(c.commodity_id))
    // If the enabled set is disjoint from the active catalog (e.g. enabled rows
    // all reference now-inactive commodities), don't blank the picker — show all.
    return filtered.length === 0 ? all : filtered
  }, [commoditiesQ.data, enabledQ.data])
  return { ...commoditiesQ, data, isLoading: commoditiesQ.isLoading || enabledQ.isLoading }
}

// Client mirror of the DB resolve_commodity() (mig 1030): map a raw
// variety / alias / commodity name to the canonical commodities.name, or null.
// Used at run-create so we never snapshot an ambiguous PPECB group code
// (e.g. "CI" = all citrus, "GR" = grapes) into runs.commodity. Exact-match
// only — same semantics as the DB function, no substring guessing. Degrades
// gracefully offline: commodity-name matches still work even if the alias
// table is not cached (the DB trigger canonicalises again on write).
export function useCommodityResolver() {
  const { data: commodities = [] } = useCommodities()
  const { data: aliases = [] } = useQuery({
    queryKey: ['commodity-aliases'],
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodity_aliases')
        .select('alias, commodities!inner(name)')
      if (error) throw error
      return (data ?? []) as { alias: string; commodities: { name: string } | { name: string }[] }[]
    },
  })
  return useMemo(() => {
    // Mirror of normalise_code(): upper + trim + collapse internal whitespace.
    const norm = (s: string) => s.trim().toUpperCase().replace(/\s+/g, ' ')
    const names = new Set(commodities.map(c => c.name))
    const aliasMap = new Map<string, string>()
    for (const a of aliases) {
      const cn = Array.isArray(a.commodities) ? a.commodities[0]?.name : a.commodities?.name
      if (cn) aliasMap.set(a.alias, cn)
    }
    return (raw: string | null | undefined): string | null => {
      if (!raw || !raw.trim()) return null
      const k = norm(raw)
      if (names.has(k)) return k          // exact commodity name wins
      return aliasMap.get(k) ?? null      // else alias → canonical name
    }
  }, [commodities, aliases])
}

const NO_BLOCKS: readonly Block[] = []

// Is this delivery line's fruit drenched? One answer for the Gate Intake form
// and the delivery edit screen: the variety first, then the commodity of the
// line's block, looked up in the PUC's blocks (the same useBlocks list the
// intake block picker reads, so on either screen it is a cache hit). The rule
// is drenchForLine in src/lib/intakePath.ts. Unknown fruit answers
// usesDrench: true.
export function useLineDrench(pucId: string | null | undefined) {
  const { data: commodities = [] } = useCommodities()
  const resolve = useCommodityResolver()
  const { data: blocks = NO_BLOCKS } = useBlocks(pucId || null)
  return useCallback(
    (variety: string | null | undefined, blockId: string | null | undefined) =>
      drenchForLine(commodities, resolve, blocks, variety, blockId),
    [commodities, resolve, blocks])
}

// Sizes for a single commodity, looked up by name (matches the snapshot
// stored on runs.commodity / blocks.commodity). Empty array when the
// commodity is unknown so callers can fall back to legacy constants.
export function useCommoditySizes(commodityName: string | null | undefined) {
  return useQuery({
    queryKey: ['commodity-sizes', commodityName ?? ''],
    enabled: !!commodityName,
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodity_sizes')
        .select('size_id, commodity_id, size_code, diameter_min_mm, diameter_max_mm, reference_count, sort_order, is_active, commodities!inner(name)')
        .eq('is_active', true)
        .eq('commodities.name', commodityName!)
        .order('sort_order')
      if (error) throw error
      return (data ?? []).map((r: { size_id: string; commodity_id: string; size_code: string; diameter_min_mm: number | null; diameter_max_mm: number | null; reference_count: number | null; sort_order: number; is_active: boolean }) => ({
        size_id:         r.size_id,
        commodity_id:    r.commodity_id,
        size_code:       r.size_code,
        diameter_min_mm: r.diameter_min_mm,
        diameter_max_mm: r.diameter_max_mm,
        reference_count: r.reference_count,
        sort_order:      r.sort_order,
        is_active:       r.is_active,
      })) as CommoditySize[]
    },
  })
}

// INTAKE grading classes for a commodity (mig 20260813100000) — Small / Medium
// / Large as a berry load is sorted when it reaches the gate.
//
// Deliberately NOT useCommoditySizes: for berries commodity_sizes holds
// PACK-FORMAT punnet weights (250g, Clamshell 500g) that feed pallet lines,
// grade prices and the QC scope picker. These are a different axis and live in
// their own tenant-owned registry, so "Small" never leaks into a pack-format
// picker and a punnet weight never appears on the intake split.
//
// Empty array = this tenant hasn't configured a split for the commodity, which
// is what keeps the intake page unchanged for everyone who doesn't want one
// (render-on-data-presence). Seeded for STRAWBERRIES only.
export function useCommodityIntakeSizes(commodityName: string | null | undefined) {
  return useQuery({
    queryKey: ['commodity-intake-sizes', commodityName ?? ''],
    enabled: !!commodityName,
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodity_intake_sizes')
        .select('intake_size_id, commodity_id, code, sort_order, is_active, commodities!inner(name)')
        .eq('is_active', true)
        .eq('commodities.name', commodityName!)
        .order('sort_order')
      if (error) throw error
      return (data ?? []).map((r: { intake_size_id: string; commodity_id: string; code: string; sort_order: number; is_active: boolean }) => ({
        intake_size_id: r.intake_size_id,
        commodity_id:   r.commodity_id,
        code:           r.code,
        sort_order:     r.sort_order,
        is_active:      r.is_active,
      })) as CommodityIntakeSize[]
    },
  })
}

// All active sizes across every commodity, distinct by size_code, used
// when a UI needs a generic size picker without binding to one commodity
// (e.g. the QC template scope picker, where a single template might apply
// to a citrus count and a deciduous count).
export function useAllCommoditySizes() {
  return useQuery({
    queryKey: ['all-commodity-sizes'],
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodity_sizes')
        .select('size_code, sort_order')
        .eq('is_active', true)
        .order('sort_order')
      if (error) throw error
      const seen = new Set<string>()
      const out: string[] = []
      for (const r of (data ?? []) as { size_code: string }[]) {
        if (!seen.has(r.size_code)) { seen.add(r.size_code); out.push(r.size_code) }
      }
      return out
    },
  })
}

// Every distinct active grade code across all commodities (mig 832). Used by
// admin scope-pickers (e.g. pallet-mark grade chips) that must offer grades for
// any commodity — citrus 1/2/P, blueberry Class 1/Class 2/Juice/Reject, etc.
export function useAllCommodityGrades() {
  return useQuery({
    queryKey: ['all-commodity-grades'],
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodity_grades')
        .select('code, sort_order')
        .eq('is_active', true)
        .order('sort_order')
      if (error) throw error
      const seen = new Set<string>()
      const out: string[] = []
      for (const r of (data ?? []) as { code: string }[]) {
        if (!seen.has(r.code)) { seen.add(r.code); out.push(r.code) }
      }
      return out
    },
  })
}

// Grades for a single commodity, looked up by name (mig 832). Empty array
// when the commodity is unknown or unseeded so callers can fall back to the
// legacy GRADES constant — same precedence as useCommoditySizes.
export function useCommodityGrades(commodityName: string | null | undefined) {
  return useQuery({
    queryKey: ['commodity-grades', commodityName ?? ''],
    enabled: !!commodityName,
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodity_grades')
        .select('grade_id, commodity_id, code, sort_order, is_active, commodities!inner(name)')
        .eq('is_active', true)
        .eq('commodities.name', commodityName!)
        .order('sort_order')
      if (error) throw error
      return (data ?? []).map((r: { grade_id: string; commodity_id: string; code: string; sort_order: number; is_active: boolean }) => ({
        grade_id:     r.grade_id,
        commodity_id: r.commodity_id,
        code:         r.code,
        sort_order:   r.sort_order,
        is_active:    r.is_active,
      })) as CommodityGrade[]
    },
  })
}

// Pack formats for a single commodity, looked up by name (mig 832). Same
// empty-array-when-unseeded contract as the grades/sizes hooks.
export function useCommodityPackFormats(commodityName: string | null | undefined) {
  return useQuery({
    queryKey: ['commodity-pack-formats', commodityName ?? ''],
    enabled: !!commodityName,
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodity_pack_formats')
        .select('format_id, commodity_id, code, label, channel, sort_order, is_active, commodities!inner(name)')
        .eq('is_active', true)
        .eq('commodities.name', commodityName!)
        // code tiebreak keeps the order deterministic if an admin ever gives
        // two formats the same sort_order.
        .order('sort_order')
        .order('code')
      if (error) throw error
      return (data ?? []).map((r: { format_id: string; commodity_id: string; code: string; label: string | null; channel: 'EXPORT' | 'LOCAL' | 'ANY'; sort_order: number; is_active: boolean }) => ({
        format_id:    r.format_id,
        commodity_id: r.commodity_id,
        code:         r.code,
        label:        r.label,
        channel:      r.channel,
        sort_order:   r.sort_order,
        is_active:    r.is_active,
      })) as CommodityPackFormat[]
    },
  })
}

// Varieties for a single commodity, looked up by name.
export function useCommodityVarieties(commodityName: string | null | undefined) {
  return useQuery({
    queryKey: ['commodity-varieties', commodityName ?? ''],
    enabled: !!commodityName,
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodity_varieties')
        .select('variety_id, commodity_id, name, code, sort_order, is_active, default_placement, commodities!inner(name)')
        .eq('is_active', true)
        .eq('commodities.name', commodityName!)
        .order('sort_order')
      if (error) throw error
      return (data ?? []).map((r: { variety_id: string; commodity_id: string; name: string; code: string | null; sort_order: number; is_active: boolean; default_placement: 'COLDROOM' | 'LOADING_BAY' | null }) => ({
        variety_id:        r.variety_id,
        commodity_id:      r.commodity_id,
        name:              r.name,
        code:              r.code,
        sort_order:        r.sort_order,
        is_active:         r.is_active,
        default_placement: r.default_placement,
      })) as CommodityVariety[]
    },
  })
}

// Map every variety NAME → its default_placement. Used by the Pallets list,
// which only has variety strings and needs to render the placement UI per
// row without joining each pallet to commodity_varieties.
export function useVarietyPlacementMap() {
  return useQuery({
    queryKey: ['variety-placement-map'],
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commodity_varieties')
        .select('name, default_placement')
        .eq('is_active', true)
      if (error) throw error
      const map = new Map<string, 'COLDROOM' | 'LOADING_BAY' | null>()
      for (const r of (data ?? []) as { name: string; default_placement: 'COLDROOM' | 'LOADING_BAY' | null }[]) {
        // Variety names can collide across commodities; whichever placement
        // is more restrictive (COLDROOM > LOADING_BAY > NULL) wins.
        const cur = map.get(r.name)
        const next = r.default_placement
        if (cur === 'COLDROOM') continue
        if (cur === 'LOADING_BAY' && next == null) continue
        map.set(r.name, next)
      }
      return map
    },
  })
}

const STANDARD_LIST_TABLES = ['commodity_sizes', 'commodity_grades', 'commodity_pack_formats', 'commodity_intake_sizes'] as const

/** Rows the tenant holds in the four standard lists for one fruit. */
export async function countStandardRows(tenantId: string, commodityId: string): Promise<number> {
  let n = 0
  for (const table of STANDARD_LIST_TABLES) {
    const res = await supabase.from(table)
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId).eq('commodity_id', commodityId)
    n += countOrThrow(res)
  }
  return n
}

export function handleItMessage(name: string, arrived: number): string {
  return arrived > 0
    ? `${name} is switched on. ${arrived} standard ${arrived === 1 ? 'entry' : 'entries'} (sizes, grades, pack formats, intake sizes) came with it. You can change all of them below. Pack units are not brought; set those up yourself.`
    : `${name} is switched on. The standard lists hold nothing for this fruit, so add its sizes, grades and pack formats yourself below.`
}

